import { useInfiniteQuery } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

import Button from '@/components/Button';
import FullScreenButton from '@/components/FullScreenButton';
import InfiniteLoader from '@/components/InfiniteLoader';
import { SrOnly } from '@/components/Layout/Parts';
import ScrollableTable from '@/components/ScrollableTable';
import { SortableTh, Table, TableContent, Th } from '@/components/Table';
import useDidUpdate from '@/hooks/useDidUpdate';
import InvoicesMemberModal from '@/pages/InvoiceList/components/InvoicesMemberModal';
import InvoicesSearchBar from '@/pages/InvoiceList/components/InvoicesSearchBar';
import {
  InvoicesMemberResource,
  PaginatedResponse,
  SortState,
} from '@/types/resources';
import { api } from '@/utils/api';
import { SortDirection } from '@/utils/enums';

import InvoicesPreferencesTableRow from './InvoicesPreferencesTableRow';

const DEFAULT_SORT_STATE: SortState = {
  direction: SortDirection.ASC,
  property: 'name',
};

const StyledTable = styled(Table)`
  th,
  td {
    padding: 16px 12px;
    font-size: 11px;
    min-width: 120px;
    &:nth-child(1) {
      padding-left: 24px;
      position: sticky;
      z-index: 1;
      background-color: ${({ theme }) => theme.colors.white};
      left: 0;
      top: 0;
      bottom: 0;
    }
    &:nth-child(4) {
      min-width: 60px;
      width: 60px;
      z-index: 1;
      position: relative;
    }
  }
  td {
    padding-top: 20px;
    padding-bottom: 20px;
  }
`;

const ButtonsHolder = styled.div`
  justify-content: flex-end;
  margin-bottom: 12px;
  gap: 8px;
  display: flex;
`;

const InvoicesPreferencesTable = () => {
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT_STATE);

  const [showMemberModal, setShowMemberModal] = useState(false);

  const [search, setSearch] = useState<string>('');

  const fetchMembers = ({ pageParam = '/v1/payments/members' }) => {
    return api.get<never, PaginatedResponse<InvoicesMemberResource>>(
      pageParam,
      {
        params: {
          ...(!!search && {
            'filter[search]': search,
          }),
          ...(sort.direction !== 'DEFAULT' && {
            sort: `${sort.direction === 'DESC' ? '-' : ''}${sort.property}`,
          }),
        },
      },
    );
  };

  const {
    data,
    isError,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    remove,
    refetch,
    fetchNextPage,
  } = useInfiniteQuery<PaginatedResponse<InvoicesMemberResource>>(
    ['invoices_members'],
    fetchMembers,
    {
      getNextPageParam: (lastPage) => {
        return lastPage.links.next;
      },
    },
  );

  const members = useMemo(
    () =>
      Object.keys(data?.pages || {}).reduce<InvoicesMemberResource[]>(
        (acc, currKey: any) => {
          return [...acc, ...data!.pages[currKey].data];
        },
        [],
      ),
    [data],
  );

  useDidUpdate(() => {
    remove();
    refetch();
  }, [sort, search]);

  const { state } = useLocation();

  useEffect(() => {
    if (state) {
      setShowMemberModal(true);
    }
  }, [state]);

  return (
    <>
      <ButtonsHolder>
        <Button onClick={() => setShowMemberModal(true)} $small $type="action">
          <svg
            width="14"
            height="15"
            viewBox="0 0 14 15"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3 7.5H11"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M7 3.5L7 11.5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Add receiver
        </Button>
        <FullScreenButton />
      </ButtonsHolder>
      <InvoicesSearchBar
        setValue={setSearch}
        placeholder="Search by receiver or true receiver"
      />
      <ScrollableTable>
        <StyledTable>
          <thead>
            <tr>
              <SortableTh
                property="name"
                title="Receiver"
                setSort={setSort}
                sort={sort}
              />
              <Th>True Receiver</Th>
              <SortableTh
                property="invoices_count"
                title="Payments Received"
                setSort={setSort}
                sort={sort}
              />
              <Th>
                <SrOnly>Actions</SrOnly>
              </Th>
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              colSpan={4}
              isError={isError}
              hasData={!!members?.length}
            >
              {members?.map((member) => (
                <InvoicesPreferencesTableRow key={member.id} member={member} />
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
      </ScrollableTable>
      <AnimatePresence>
        {showMemberModal && (
          <InvoicesMemberModal
            onClose={() => setShowMemberModal(false)}
            memberId={state?.newMemberId}
          />
        )}
      </AnimatePresence>
      <InfiniteLoader
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />
    </>
  );
};

export default InvoicesPreferencesTable;
